import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useMemo } from 'react';
import { FormSpy } from 'react-final-form';

import {
  getActKnoExpertBlockId,
  getActKnoExpertsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnmErknmFormExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { IKnmErknmFormActExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormActExpert';
import { IKnmActExpertSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActExpertSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './ErknmActParticipants.module.scss';

interface IErknmActExpertsProps {
  id: string;
  index?: number;
  name: string;
  isDisabledField: boolean;
  expertsErknm: IKnmErknmFormExpert[] | undefined;
  involvedExperts: IKnmErknmFormActExpert[] | undefined;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

export const ErknmActExperts = ({
  id,
  index,
  name,
  formRef,
  isDisabledField,
  expertsErknm,
  involvedExperts,
}: IErknmActExpertsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const expertsBlockId = getActKnoExpertsBlockId(id);

  const addField = (fieldName: string) => () =>
    !isDisabledField && push && push(`${name}.${fieldName}`, { listId: uuidv4(), isNew: true });
  const makeRemoveField = (fieldName: string, i: number) => () => {
    !isDisabledField && remove && remove(`${name}.${fieldName}`, i);
  };

  /**
   * Фильтрованный список вариантов для выбора эксперта, без ранее использованных
   */
  const expertsErknmOptionsFiltered = useMemo(() => {
    return (
      expertsErknm
        ?.filter(expert =>
          Array.isArray(involvedExperts) ? !involvedExperts?.find(i => i.expert?.value.listId === expert.listId) : true,
        )
        .map(expert => {
          return {
            id: expert.guid || '',
            value: expert,
            label: `${expert.title} - ${expert.type?.label}`,
          } as IKnmActExpertSelectOption;
        }) || []
    );
  }, [expertsErknm, JSON.stringify(involvedExperts)]);

  return (
    <>
      <FieldBlock
        id={expertsBlockId}
        label={'Привлеченные эксперты'}
        className={styles.GroupMarginBottom}
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={isDisabledField}
            onClick={addField('involvedExperts')}
          />
        }
      >
        <FieldArray name={`${name}.involvedExperts`}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormExpert, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((fieldName, i) => {
                  const fieldValue: IKnmErknmFormActExpert = fields.value?.[i];
                  const expertId = `${getActKnoExpertBlockId(id, fieldValue.listId)}.expert`;

                  return (
                    <Row key={expertId} margin="medium" isListItem notSpace>
                      <SelectInputField
                        className={styles.SelectInputExperts}
                        name={`${fieldName}.expert`}
                        id={expertId}
                        placeholder={'Выберите эксперта'}
                        options={expertsErknmOptionsFiltered}
                        isSearchable
                        disabled={isDisabledField}
                        size="medium"
                      />
                      <CloseButton
                        className="shared-deleting-row-btn"
                        onClick={makeRemoveField('involvedExperts', i)}
                      />
                    </Row>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (index !== undefined && props.errors?.acts && props.errors?.acts[index]?.involvedExperts) {
            const fieldError = props.errors.acts[index].involvedExperts;
            const isFieldTouched = props.touched?.[`acts[${index}].involvedExperts`];

            const errorMessage: string | undefined = fieldError
              ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
              : undefined;

            if (isFieldTouched && errorMessage && typeof fieldError === 'string') {
              return <p className={`${styles.expertsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>;
            }
          }
          if (index === undefined && props.errors?.act && props.errors?.act?.involvedExperts) {
            const fieldError = props.errors.act.involvedExperts;
            const isFieldTouched = props.touched?.[`act.involvedExperts`];

            const errorMessage: string | undefined = fieldError
              ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
              : undefined;

            if (isFieldTouched && errorMessage && typeof fieldError === 'string') {
              return <p className={`${styles.expertsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>;
            }
          }

          return null;
        }}
      </FormSpy>
    </>
  );
};
